import { fetchCFImage } from "@shared/cloudflare.js";
import * as stickerSetFeed from "@shared/stickerSetFeed.js";
import imageStorage from "@shared/ImageStorage.js";
import { Compositor } from "@shared/Compositor.js";
import { Product } from "@shared/Product.js";
import { RenderSeed } from "@shared/RenderSeed.js";
import { cropTransparentImage } from "@shared/imgUtil.js";
import { CYODesign } from "@shared/CYODesign.js";

function getPreviewCanvas() {
    if (typeof document === "undefined") return null;
    return document.querySelector("cyo-preview-canvas");
}

/**
 * Print utilities for generating print-ready content
 */
class CartUtilsPrint {
    /**
     * Generates a print-ready canvas for the current design
     *
     * @param {CYODesign} cyoDesign - The current design options
     * @param {HTMLElement} previewCanvasElement - Optional preview canvas element
     * @returns {Promise<HTMLCanvasElement>} The print-ready canvas
     */
    static async generatePrintReadyCanvas(cyoDesign, previewCanvasElement = null) {
        const previewCanvas = previewCanvasElement || getPreviewCanvas();
        const device = Product.device;

        // Handle grip-only mode for wallet products
        if (device?.productCategory === "wallet" &&
            cyoDesign.walletPrintSurface === CYODesign.WalletPrintSurface.grip) {
            return CartUtilsPrint.generateGripOnlyPrintCanvas(cyoDesign, previewCanvas);
        }

        // Fetch full resolution images
        const { fullBGImage, fullStickerSetImages } = await CartUtilsPrint.fetchFullResolutionImages(cyoDesign);

        // Get uploaded user images from ImageStorage
        const userImage = imageStorage.getUploadedImage();
        const transparentImage = imageStorage.getTransparentImage();

        // Create design options for print with full resolution images
        const printCYODesign = cyoDesign.clone();
        printCYODesign.stickerImages = fullStickerSetImages || [];
        printCYODesign.backgroundImage = fullBGImage || null;
        printCYODesign.transparentImage = transparentImage || null;
        printCYODesign.userImage = userImage || null;

        // Clear walletPrintSurface for non-wallet products so that wallet-specific
        // split-mode logic in CompositorPrint doesn't incorrectly fire for standalone
        // grip products (walletPrintSurface defaults to "split" on all CYODesign instances).
        if (device?.productCategory !== "wallet") {
            printCYODesign.walletPrintSurface = null;
        }

        // Verify we have a device
        if (!device) {
            throw new Error("No device available for print generation");
        }

        // Extract current transforms from preview canvas
        const layerTransformData = CartUtilsPrint.extractTransformsFromPreviewCanvas(previewCanvas);

        // Extract random seed from preview to ensure deterministic print rendering
        const randomSeed = CartUtilsPrint.extractRandomSeedFromPreview(printCYODesign, previewCanvas);

        // Check if we need independent both mode rendering
        let altDevice = null;
        let altLayerTransformData = null;
        if (printCYODesign.requiresIndependentSurfaceRendering?.(device) && previewCanvas?.altCompositor) {
            altDevice = previewCanvas.altCompositor._device;
            altLayerTransformData = CartUtilsPrint.extractTransformsFromAltCompositor(previewCanvas);
        }

        // Generate the print-ready canvas with deterministic randomness, skipping mask
        const printCanvas = await Compositor.createPrintReadyCanvas(
            device,
            printCYODesign,
            layerTransformData,
            device.imageID,
            randomSeed,
            altDevice,
            altLayerTransformData
        );

        return printCanvas;
    }

    /**
     * Calculates the effective DPI for the user photo as rendered on the preview canvas.
     * Assumes the original photo is 300 DPI, measures the drawn size after all transforms,
     * then divides 300 by the larger scale factor (displayed / original) to get effective DPI.
     */
    static calculateEffectiveUserImageDpi(cyoDesign = CYODesign.current, previewCanvasElement = null) {
        try {
            const previewCanvas = previewCanvasElement || getPreviewCanvas();
            const compositor = previewCanvas?.compositor;
            if (!previewCanvas || !compositor) {
                return null;
            }

            const layerObjects = compositor.getLayerObjects?.() ? compositor.getLayerObjects() : compositor._layerObjects;
            if (!layerObjects) {
                return null;
            }

            const userImageLayer = layerObjects.userImage;
            if (!userImageLayer || !userImageLayer.visible) {
                return null;
            }

            const needsTransparent = typeof cyoDesign?.placementAndStyleRequiresTransparent === "function" ? cyoDesign.placementAndStyleRequiresTransparent() : false;
            const transparentImg = imageStorage.getTransparentImage ? imageStorage.getTransparentImage() : imageStorage.transparentImage;
            const uploadedImg = imageStorage.getUploadedImage ? imageStorage.getUploadedImage() : imageStorage.uploadedImage;
            const baseImg = needsTransparent && transparentImg ? transparentImg : uploadedImg;
            if (!baseImg) {
                return null;
            }

            const originalWidth = baseImg.width || 0;
            const originalHeight = baseImg.height || 0;
            if (originalWidth <= 0 || originalHeight <= 0) {
                return null;
            }

            const width = compositor.width;
            const height = compositor.height;

            userImageLayer.render(width, height);
            const layerCanvas = userImageLayer.canvas;
            if (!layerCanvas) {
                return null;
            }

            const tempCanvas = document.createElement("canvas");
            tempCanvas.width = width;
            tempCanvas.height = height;
            const tempCtx = tempCanvas.getContext("2d", { alpha: true });

            if (layerCanvas._isExpandedCanvas) {
                const baseOffsetX = -(layerCanvas._viewportOffsetX || 0);
                const baseOffsetY = -(layerCanvas._viewportOffsetY || 0);

                if (userImageLayer.transform && (userImageLayer.transform.scale !== 1 || userImageLayer.transform.rotation !== 0 || userImageLayer.transform.translateX !== 0 || userImageLayer.transform.translateY !== 0)) {
                    const pivotX = layerCanvas._rotationPivotX ?? (layerCanvas._originalCanvasWidth ? layerCanvas._originalCanvasWidth / 2 : width / 2);
                    const pivotY = layerCanvas._rotationPivotY ?? (layerCanvas._originalCanvasHeight ? layerCanvas._originalCanvasHeight / 2 : height / 2);
                    tempCtx.save();
                    tempCtx.translate(pivotX + userImageLayer.transform.translateX, pivotY + userImageLayer.transform.translateY);
                    tempCtx.rotate(userImageLayer.transform.rotation * Math.PI / 180);
                    tempCtx.scale(userImageLayer.transform.scale, userImageLayer.transform.scale);
                    tempCtx.translate(-pivotX, -pivotY);
                    tempCtx.drawImage(layerCanvas, baseOffsetX, baseOffsetY);
                    tempCtx.restore();
                } else {
                    tempCtx.drawImage(layerCanvas, baseOffsetX, baseOffsetY);
                }
            } else {
                if (userImageLayer.transform && (userImageLayer.transform.scale !== 1 || userImageLayer.transform.rotation !== 0 || userImageLayer.transform.translateX !== 0 || userImageLayer.transform.translateY !== 0)) {
                    const centerX = width / 2;
                    const centerY = height / 2;
                    tempCtx.save();
                    tempCtx.translate(centerX + userImageLayer.transform.translateX, centerY + userImageLayer.transform.translateY);
                    tempCtx.rotate(userImageLayer.transform.rotation * Math.PI / 180);
                    tempCtx.scale(userImageLayer.transform.scale, userImageLayer.transform.scale);
                    tempCtx.translate(-centerX, -centerY);
                    tempCtx.drawImage(layerCanvas, 0, 0);
                    tempCtx.restore();
                } else {
                    tempCtx.drawImage(layerCanvas, 0, 0);
                }
            }

            const [_cropped, bounds] = cropTransparentImage(tempCanvas, true);
            if (!bounds) {
                return null;
            }

            const dispW = bounds[2];
            const dispH = bounds[3];
            if (dispW <= 0 || dispH <= 0) {
                return null;
            }

            const scaleX = dispW / originalWidth;
            const scaleY = dispH / originalHeight;
            const finalScale = Math.max(scaleX, scaleY);
            if (!finalScale || !isFinite(finalScale) || finalScale <= 0) {
                return null;
            }

            const assumedOriginalDpi = 300;
            const effectiveDpi = assumedOriginalDpi / finalScale;
            return effectiveDpi;
        } catch (error) {
            console.error("Failed to compute effective user image DPI:", error);
            return null;
        }
    }


    /**
     * Fetches full resolution images for print
     *
     * @param {CYODesign} cyoDesign - The current design options
     * @returns {Promise<Object>} Object containing fullBGImage and fullStickerSetImages
     */
    static async fetchFullResolutionImages(cyoDesign) {
        let fullBGImage;
        let fullStickerSetImages;

        // Fetch full resolution background image from Cloudflare if galleryImageID is set (production case)
        // Otherwise, fall back to using existing backgroundImage (test case with local files)
        if (cyoDesign.galleryImageID) {
            // Use full resolution for print - increase from 400 to larger size
            // TODO: Determine optimal print resolution
            fullBGImage = await fetchCFImage(cyoDesign.galleryImageID, 2000, "webp", 100, 1);
        } else if (cyoDesign.backgroundImage) {
            fullBGImage = cyoDesign.backgroundImage;
        }

        // Fetch full resolution sticker set images if selected
        if (cyoDesign.stickerSet) {
            fullStickerSetImages = await stickerSetFeed.imageArrayForSetWithID(cyoDesign.stickerSet.id, 100);
        }

        return { fullBGImage, fullStickerSetImages };
    }

    /**
     * Extracts current transforms from the preview canvas
     *
     * @param {HTMLElement} previewCanvas - Optional preview canvas element
     * @returns {Object|null} Layer transform data or null if no transforms found.
     *          Includes sourceWidth/sourceHeight for scaling transforms to different canvas sizes.
     */
    static extractTransformsFromPreviewCanvas(previewCanvas = null) {
        const canvas = previewCanvas || getPreviewCanvas();
        if (!canvas || !canvas.compositor) {
            return null;
        }

        const layerObjects = canvas.compositor.getLayerObjects();
        const layerTransformData = {};

        // Capture the source canvas dimensions for scaling transforms to print canvas
        layerTransformData.sourceWidth = canvas.compositor.width;
        layerTransformData.sourceHeight = canvas.compositor.height;

        // Preserve user image transforms if they exist
        if (layerObjects.userImage && layerObjects.userImage.transform) {
            layerTransformData.userImage = {
                scale: layerObjects.userImage.transform.scale,
                rotate: layerObjects.userImage.transform.rotation,
                translateX: layerObjects.userImage.transform.translateX,
                translateY: layerObjects.userImage.transform.translateY
            };
            // Capture visualCenter for non-expanded canvases (used as transform origin)
            if (layerObjects.userImage.visualCenter) {
                layerTransformData.userImage.visualCenterX = layerObjects.userImage.visualCenter.x;
                layerTransformData.userImage.visualCenterY = layerObjects.userImage.visualCenter.y;
            }
        }

        // Preserve sticker transforms if they exist
        if (layerObjects.stickers && layerObjects.stickers.transform) {
            layerTransformData.stickers = {
                scale: layerObjects.stickers.transform.scale,
                rotate: layerObjects.stickers.transform.rotation,
                translateX: layerObjects.stickers.transform.translateX,
                translateY: layerObjects.stickers.transform.translateY
            };
        }

        return Object.keys(layerTransformData).length > 0 ? layerTransformData : null;
    }

    /**
     * Extracts layer transforms from the altCompositor (grip device) in a PreviewCanvas
     *
     * @param {HTMLElement} previewCanvas - Optional preview canvas element
     * @returns {Object|null} Layer transform data or null if no transforms found.
     */
    static extractTransformsFromAltCompositor(previewCanvas = null) {
        const canvas = previewCanvas || getPreviewCanvas();
        if (!canvas || !canvas.altCompositor) {
            return null;
        }

        const layerObjects = canvas.altCompositor._layerObjects;
        if (!layerObjects) {
            return null;
        }

        const layerTransformData = {};

        // Capture the source canvas dimensions for scaling transforms to print canvas
        layerTransformData.sourceWidth = canvas.altCompositor.width;
        layerTransformData.sourceHeight = canvas.altCompositor.height;

        // Preserve user image transforms if they exist
        if (layerObjects.userImage && layerObjects.userImage.transform) {
            layerTransformData.userImage = {
                scale: layerObjects.userImage.transform.scale,
                rotate: layerObjects.userImage.transform.rotation,
                translateX: layerObjects.userImage.transform.translateX,
                translateY: layerObjects.userImage.transform.translateY
            };
            if (layerObjects.userImage.visualCenter) {
                layerTransformData.userImage.visualCenterX = layerObjects.userImage.visualCenter.x;
                layerTransformData.userImage.visualCenterY = layerObjects.userImage.visualCenter.y;
            }
        }

        // Preserve sticker transforms if they exist
        if (layerObjects.stickers && layerObjects.stickers.transform) {
            layerTransformData.stickers = {
                scale: layerObjects.stickers.transform.scale,
                rotate: layerObjects.stickers.transform.rotation,
                translateX: layerObjects.stickers.transform.translateX,
                translateY: layerObjects.stickers.transform.translateY
            };
        }

        return Object.keys(layerTransformData).length > 0 ? layerTransformData : null;
    }

    /**
     * Extracts random seed from the preview to ensure deterministic print rendering
     *
     * @param {CYODesign} cyoDesign - The design options for seed generation
     * @param {HTMLElement} previewCanvas - Optional preview canvas element
     * @returns {number|null} Random seed used by preview, or generated seed if preview not available
     */
    static extractRandomSeedFromPreview(cyoDesign, previewCanvas = null) {
        const canvas = previewCanvas || getPreviewCanvas();

        if (canvas && canvas.contentRenderer && canvas.contentRenderer.randomState) {
            const existingSeed = canvas.contentRenderer.randomState.getOriginalSeed();
            return existingSeed;
        }

        // Try alternative ways to find ContentRenderer

        // Check if there's a global reference or other way to access the current ContentRenderer
        if (window.currentContentRenderer && window.currentContentRenderer.randomState) {
            const existingSeed = window.currentContentRenderer.randomState.getOriginalSeed();
            return existingSeed;
        }

        // If we can't get the seed from preview, generate one deterministically from design content
        // This ensures that identical designs still produce identical results
        const generatedSeed = RenderSeed.generateSeed(cyoDesign);

        return generatedSeed;
    }

    /**
     * Generates a print-ready canvas for the grip in "both" mode.
     * - User image: extracted from wallet-dimension canvas (spans both surfaces)
     * - Stickers: rendered independently using grip device
     *
     * @param {CYODesign} cyoDesign - The current design options
     * @param {Object} previewCanvasElement - Preview canvas element for extracting transforms
     * @returns {Promise<HTMLCanvasElement>} The grip print canvas for "both" mode
     */
    static async generateBothModeGripPrintCanvas(cyoDesign, previewCanvasElement = null) {
        const previewCanvas = previewCanvasElement || getPreviewCanvas();

        const { fullBGImage } = await CartUtilsPrint.fetchFullResolutionImages(cyoDesign);
        const userImage = imageStorage.getUploadedImage();
        const transparentImage = imageStorage.getTransparentImage();

        const layerTransformData = CartUtilsPrint.extractTransformsFromPreviewCanvas(previewCanvas);
        const randomSeed = CartUtilsPrint.extractRandomSeedFromPreview(cyoDesign, previewCanvas);

        // Check if we need special z-order (user image ON TOP of stickers)
        const isRemoveBgCenterOrBottom = cyoDesign.subjectStyle === CYODesign.Style.removeBackground &&
            (cyoDesign.subjectPlacement === CYODesign.Placement.center || cyoDesign.subjectPlacement === CYODesign.Placement.bottom);

        // Skip stickers on grip when subjectStyle is "full" (Full Image mode)
        const isFullImageStyle = cyoDesign.subjectStyle === CYODesign.Style.full;
        const hasStickersForGrip = cyoDesign.stickerSet && previewCanvas?.altCompositor && !isFullImageStyle;

        // Helper to generate sticker canvas for grip
        const generateStickerCanvas = async () => {
            if (!hasStickersForGrip) return null;

            const { fullStickerSetImages } = await CartUtilsPrint.fetchFullResolutionImages(cyoDesign);
            if (!fullStickerSetImages?.length) return null;

            const gripDevice = previewCanvas.altCompositor._device;
            if (!gripDevice) return null;

            const stickerCYODesign = cyoDesign.clone();
            stickerCYODesign.stickerImages = fullStickerSetImages;
            stickerCYODesign.backgroundImage = null;
            stickerCYODesign.userImage = null;
            stickerCYODesign.transparentImage = null;

            const altLayerTransformData = CartUtilsPrint.extractTransformsFromAltCompositor(previewCanvas);

            return {
                canvas: await Compositor.createPrintReadyCanvas(
                    gripDevice,
                    stickerCYODesign,
                    altLayerTransformData,
                    gripDevice.imageID,
                    randomSeed,
                    null,
                    null
                ),
                device: gripDevice
            };
        };

        // For removeBackground + center/bottom: generate separate bg and user image canvases
        if (isRemoveBgCenterOrBottom) {
            // Generate background-only canvas
            const bgOnlyDesign = cyoDesign.clone();
            bgOnlyDesign.stickerSet = null;
            bgOnlyDesign.stickerImages = [];
            bgOnlyDesign.backgroundImage = fullBGImage || null;
            bgOnlyDesign.transparentImage = null;
            bgOnlyDesign.userImage = null;

            const bgOnlyCanvas = await Compositor.createPrintReadyCanvas(
                Product.device,
                bgOnlyDesign,
                layerTransformData,
                Product.device.imageID,
                randomSeed,
                null,
                null
            );

            // Generate user image-only canvas (no background)
            const userImageOnlyDesign = cyoDesign.clone();
            userImageOnlyDesign.stickerSet = null;
            userImageOnlyDesign.stickerImages = [];
            userImageOnlyDesign.backgroundImage = null;
            userImageOnlyDesign.transparentImage = transparentImage || null;
            userImageOnlyDesign.userImage = userImage || null;

            const userImageOnlyCanvas = await Compositor.createPrintReadyCanvas(
                Product.device,
                userImageOnlyDesign,
                layerTransformData,
                Product.device.imageID,
                randomSeed,
                null,
                null
            );

            // Get grip bounds
            const { loadWalletGripMask, createInvertedMask, getOpaqueBounds } = await import("@shared/CompositorMasks.js");
            const walletGripMaskImage = await loadWalletGripMask();
            const invertedMaskCanvas = createInvertedMask(walletGripMaskImage, bgOnlyCanvas.width, bgOnlyCanvas.height);
            const gripBounds = getOpaqueBounds(invertedMaskCanvas);

            if (!gripBounds) {
                throw new Error("Could not determine grip bounds from wallet-grip mask");
            }

            // Create grip canvas
            const gripPrintCanvas = document.createElement("canvas");
            gripPrintCanvas.width = gripBounds.width;
            gripPrintCanvas.height = gripBounds.height;
            const gripCtx = gripPrintCanvas.getContext("2d", { alpha: true });

            // Draw in correct z-order: background → stickers → user image
            // 1. Draw background
            gripCtx.drawImage(
                bgOnlyCanvas,
                gripBounds.x, gripBounds.y, gripBounds.width, gripBounds.height,
                0, 0, gripBounds.width, gripBounds.height
            );

            // 2. Draw stickers
            const stickerResult = await generateStickerCanvas();
            if (stickerResult) {
                const { canvas: stickerCanvas, device: gripDevice } = stickerResult;
                const safeAreaInsets = gripDevice.safeAreaInsets || [0, 0, 0, 0];
                const contentWidthRatio = 1 - safeAreaInsets[3] - safeAreaInsets[1];
                const contentHeightRatio = 1 - safeAreaInsets[0] - safeAreaInsets[2];
                const scaleX = gripBounds.width / (stickerCanvas.width * contentWidthRatio);
                const scaleY = gripBounds.height / (stickerCanvas.height * contentHeightRatio);
                const scale = Math.min(scaleX, scaleY);
                const scaledWidth = stickerCanvas.width * scale;
                const scaledHeight = stickerCanvas.height * scale;
                const contentOffsetX = safeAreaInsets[3] * scaledWidth;
                const contentOffsetY = safeAreaInsets[0] * scaledHeight;
                const offsetX = (gripBounds.width - scaledWidth * contentWidthRatio) / 2 - contentOffsetX;
                const offsetY = (gripBounds.height - scaledHeight * contentHeightRatio) / 2 - contentOffsetY;
                gripCtx.drawImage(stickerCanvas, offsetX, offsetY, scaledWidth, scaledHeight);
            }

            // 3. Draw user image on top
            gripCtx.drawImage(
                userImageOnlyCanvas,
                gripBounds.x, gripBounds.y, gripBounds.width, gripBounds.height,
                0, 0, gripBounds.width, gripBounds.height
            );

            return gripPrintCanvas;
        }

        // For other styles: original behavior (bg + user image first, stickers on top)
        const printCYODesignNoStickers = cyoDesign.clone();
        printCYODesignNoStickers.stickerSet = null;
        printCYODesignNoStickers.stickerImages = [];
        printCYODesignNoStickers.backgroundImage = fullBGImage || null;
        printCYODesignNoStickers.transparentImage = transparentImage || null;
        printCYODesignNoStickers.userImage = userImage || null;

        const walletNoStickersCanvas = await Compositor.createPrintReadyCanvas(
            Product.device,
            printCYODesignNoStickers,
            layerTransformData,
            Product.device.imageID,
            randomSeed,
            null,
            null
        );

        // Get grip bounds
        const { loadWalletGripMask, createInvertedMask, getOpaqueBounds } = await import("@shared/CompositorMasks.js");
        const walletGripMaskImage = await loadWalletGripMask();
        const invertedMaskCanvas = createInvertedMask(walletGripMaskImage, walletNoStickersCanvas.width, walletNoStickersCanvas.height);
        const gripBounds = getOpaqueBounds(invertedMaskCanvas);

        if (!gripBounds) {
            throw new Error("Could not determine grip bounds from wallet-grip mask");
        }

        // Create grip canvas
        const gripPrintCanvas = document.createElement("canvas");
        gripPrintCanvas.width = gripBounds.width;
        gripPrintCanvas.height = gripBounds.height;
        const gripCtx = gripPrintCanvas.getContext("2d", { alpha: true });

        // Draw bg + user image
        gripCtx.drawImage(
            walletNoStickersCanvas,
            gripBounds.x, gripBounds.y, gripBounds.width, gripBounds.height,
            0, 0, gripBounds.width, gripBounds.height
        );

        // Draw stickers on top
        const stickerResult = await generateStickerCanvas();
        if (stickerResult) {
            const { canvas: stickerCanvas, device: gripDevice } = stickerResult;
            const safeAreaInsets = gripDevice.safeAreaInsets || [0, 0, 0, 0];
            const contentWidthRatio = 1 - safeAreaInsets[3] - safeAreaInsets[1];
            const contentHeightRatio = 1 - safeAreaInsets[0] - safeAreaInsets[2];
            const scaleX = gripBounds.width / (stickerCanvas.width * contentWidthRatio);
            const scaleY = gripBounds.height / (stickerCanvas.height * contentHeightRatio);
            const scale = Math.min(scaleX, scaleY);
            const scaledWidth = stickerCanvas.width * scale;
            const scaledHeight = stickerCanvas.height * scale;
            const contentOffsetX = safeAreaInsets[3] * scaledWidth;
            const contentOffsetY = safeAreaInsets[0] * scaledHeight;
            const offsetX = (gripBounds.width - scaledWidth * contentWidthRatio) / 2 - contentOffsetX;
            const offsetY = (gripBounds.height - scaledHeight * contentHeightRatio) / 2 - contentOffsetY;
            gripCtx.drawImage(stickerCanvas, offsetX, offsetY, scaledWidth, scaledHeight);
        }

        return gripPrintCanvas;
    }

    /**
     * Generates a print-ready canvas for grip-only mode on wallet products.
     * Uses the grip device for proper dimensions and transforms.
     *
     * @param {CYODesign} cyoDesign - The current design options
     * @param {Object} previewCanvasElement - Optional preview canvas element
     * @returns {Promise<HTMLCanvasElement>} The grip-only print-ready canvas
     */
    static async generateGripOnlyPrintCanvas(cyoDesign, previewCanvasElement = null) {
        const previewCanvas = previewCanvasElement || getPreviewCanvas();

        // Fetch full resolution images
        const { fullBGImage, fullStickerSetImages } = await CartUtilsPrint.fetchFullResolutionImages(cyoDesign);

        // Get user images from ImageStorage
        const userImage = imageStorage.getUploadedImage();
        const transparentImage = imageStorage.getTransparentImage();

        // Create print design options
        const printCYODesign = cyoDesign.clone();
        printCYODesign.stickerImages = fullStickerSetImages || [];
        printCYODesign.backgroundImage = fullBGImage || null;
        printCYODesign.transparentImage = transparentImage || null;
        printCYODesign.userImage = userImage || null;

        // Get the grip device (first live grip-poptop SKU from config ordering)
        const { getFirstLiveSKUFromOrdering } = await import("@shared/productSKUFeed.js");
        const { getProductOrdering } = await import("@shared/configFeed.js");
        const gripOrdering = getProductOrdering("grip-poptop");
        const gripDevice = getFirstLiveSKUFromOrdering(gripOrdering);
        if (!gripDevice) {
            throw new Error("Grip device not found for grip-only print generation");
        }

        // Extract transforms from altCompositor
        const layerTransformData = previewCanvas?.altCompositor
            ? CartUtilsPrint.extractTransformsFromAltCompositor(previewCanvas)
            : null;

        // Extract random seed
        const randomSeed = CartUtilsPrint.extractRandomSeedFromPreview(printCYODesign, previewCanvas);

        // Generate print canvas using grip device
        return Compositor.createPrintReadyCanvas(
            gripDevice,
            printCYODesign,
            layerTransformData,
            gripDevice.imageID,
            randomSeed,
            null,
            null
        );
    }
}

export { CartUtilsPrint };
